import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { setAuthToken, subscribeTokenExpired } from '../api/httpClient';
import { api, type LoginResponse } from '../api';
import type { User } from '../types';

interface AuthContextShape {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  idToken: string | null;
  completeLogin: (data: LoginResponse) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextShape | undefined>(undefined);

const TOKEN_STORAGE_KEY = 'io-marketplace/token';
const ID_TOKEN_STORAGE_KEY = 'io-marketplace/id-token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdTokenState] = useState<string | null>(null);
  const endSessionEndpointRef = useRef<string | null | undefined>(undefined);

  const setToken = useCallback((value: string | null) => {
    setTokenState(value);
    setAuthToken(value);
    if (value) {
      setLoading(true);
      localStorage.setItem(TOKEN_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      setLoading(false);
    }
  }, [setLoading]);

  const setIdToken = useCallback((value: string | null) => {
    setIdTokenState(value);
    if (value) {
      localStorage.setItem(ID_TOKEN_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
    }
  }, []);

  const loadEndSessionEndpoint = useCallback(async () => {
    if (endSessionEndpointRef.current !== undefined) {
      return endSessionEndpointRef.current;
    }

    try {
      const metadata = await api.oidcMetadata();
      const endpoint = metadata?.endSessionEndpoint ?? null;
      endSessionEndpointRef.current = endpoint;
      return endpoint;
    } catch (error) {
      console.error('Unable to load OIDC metadata', error);
      endSessionEndpointRef.current = null;
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const profile = await api.currentUser();
      setUser(profile);
    } catch (error) {
      console.error('Unable to refresh user profile', error);
      setToken(null);
      setIdToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, setToken, setIdToken]);

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const storedIdToken = localStorage.getItem(ID_TOKEN_STORAGE_KEY);
    if (storedIdToken) {
      setIdToken(storedIdToken);
    }
    if (storedToken) {
      setToken(storedToken);
    } else {
      setLoading(false);
    }
  }, [setToken, setIdToken]);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    const unsubscribe = subscribeTokenExpired(() => {
      setToken(null);
      setIdToken(null);
      setUser(null);
    });
    return unsubscribe;
  }, [setToken, setIdToken]);

  useEffect(() => {
    void loadEndSessionEndpoint();
  }, [loadEndSessionEndpoint]);

  const completeLogin = useCallback(
    (data: LoginResponse) => {
      setToken(data.token);
      setUser(data.user);
      setIdToken(data.idToken ?? null);
    },
    [setToken, setIdToken]
  );

  const logout = useCallback(async () => {
    const postLogoutRedirectUri = `${window.location.origin}/login`;
    const idTokenSnapshot = idToken;
    let endSessionEndpoint = endSessionEndpointRef.current;

    if (endSessionEndpoint === undefined) {
      endSessionEndpoint = await loadEndSessionEndpoint();
    }

    setToken(null);
    setUser(null);
    setIdToken(null);

    if (endSessionEndpoint && idTokenSnapshot) {
      try {
        const url = new URL(endSessionEndpoint);
        url.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUri);
        url.searchParams.set('id_token_hint', idTokenSnapshot);
        window.location.href = url.toString();
        return;
      } catch (error) {
        console.error('Failed to construct IdP logout URL', error);
      }
    }

    window.location.href = postLogoutRedirectUri;
  }, [idToken, loadEndSessionEndpoint, setIdToken, setToken]);

  const value = useMemo(
    () => ({
      user,
      token,
      idToken,
      loading,
      isAuthenticated: Boolean(user && token),
      completeLogin,
      logout,
      refreshUser
    }),
    [user, token, idToken, loading, completeLogin, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
